/**
 * DefaultCard — one article in the round's stack.
 *
 * Pass 2 of the component recipe: this is the showroom's `MediaTile` on the
 * product's data — a picture with words on it, which is the unit every
 * content page in the market is built from. The whole card is one link; the
 * meta line is the fixture, the persona and the read time
 * ("FC København – AC Horsens · Fortælleren · 2 min."), set 10px in
 * `--ink-3` like every other meta line in the recipe.
 *
 * Removed in pass 2, and why:
 *   - the per-line black highlight boxes behind the headline (a literal
 *     colour in a component — recipe §2 rule 1);
 *   - the recipe chip floating on the picture (metadata is a line, not a
 *     sticker) and the separate "Kampstatistik →" CTA (the card already
 *     leads to the article, and the article leads to the match — two links
 *     on one card is the affordance the reference pages do not have);
 *   - the natural-height image, which made a column of six cards six
 *     different heights. A tile is 16:9 and the picture fills it.
 */

import { type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import type { Article } from '@aamp/shared-types';
import { useTranslation } from '../../i18n/index.js';
import { TeamColorSvgHero } from '../atoms/TeamColorSvgHero.js';
import styles from './DefaultCard.module.css';

/**
 * What the card needs from an article. The feed's `Article` satisfies it;
 * so does the match-detail article (`MatchDetailArticle` + the fixture's
 * team names), which is why the match page renders THIS card for its
 * featured article rather than a second hand-styled one.
 */
export type DefaultCardArticle = Pick<Article, 'id' | 'headline' | 'manchet'> & {
  readTimeMinutes?: number | undefined;
  journalistName?: string | undefined;
  heroImage?: Article['heroImage'];
  match: Pick<Article['match'], 'homeTeam' | 'awayTeam'>;
};

interface DefaultCardProps {
  article: DefaultCardArticle;
}

export function DefaultCard({ article }: DefaultCardProps): ReactElement {
  const { t } = useTranslation();

  const readTime =
    typeof article.readTimeMinutes === 'number'
      ? t('lobby.readTime', {
          count: article.readTimeMinutes,
          defaultValue: `${article.readTimeMinutes} min`,
        })
      : null;
  const meta = [
    article.match?.homeTeam && article.match?.awayTeam
      ? `${article.match.homeTeam} – ${article.match.awayTeam}`
      : null,
    // The engine's localised persona name. A slug is a system word.
    article.journalistName ?? null,
    readTime,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <article className={styles.card}>
      <Link
        to={`/article/${article.id}`}
        className={styles.link}
        aria-label={article.headline}
      >
        <div className={styles.image}>
          <TeamColorSvgHero
            homeKey={article.match.homeTeam}
            awayKey={article.match.awayTeam}
            eventId={article.id}
            ariaLabel={article.headline}
            className={styles.media}
          />
          {article.heroImage?.url ? (
            <img
              src={article.heroImage.url}
              alt={article.heroImage.alt}
              className={styles.media}
              loading="lazy"
              decoding="async"
              onError={(e): void => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : null}
          <span className={styles.scrim} aria-hidden="true" />
        </div>
        <div className={styles.copy}>
          <h2 className={styles.headline}>{article.headline}</h2>
          <p className={styles.manchet}>{article.manchet}</p>
          <span className={styles.meta}>{meta}</span>
        </div>
      </Link>
    </article>
  );
}

export default DefaultCard;
